import { fetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';
import {
  FilingCalendarPreviewData,
  FilingCalendarPreviewRequest,
  FilingCalendarPreviewResponse,
} from './types';

export interface UseFilingCalendarPreviewParams {
  corporationId: number;
  state?: string;
  filingFrequency?: string;
  taxPeriodStartMonth?: string;
  taxPeriodEndMonth?: string;
  enabled?: boolean;
}

export interface UseFilingCalendarPreviewResult {
  data: FilingCalendarPreviewData | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: KeyedMutator<FilingCalendarPreviewResponse>;
}

function buildRequestKey(
  params: UseFilingCalendarPreviewParams
): string | null {
  const {
    corporationId,
    state,
    filingFrequency,
    taxPeriodStartMonth,
    enabled,
  } = params;

  // Don't fetch if not enabled or missing required params
  if (enabled === false) return null;
  if (!state || !filingFrequency || !taxPeriodStartMonth) return null;

  // Return a unique key for SWR caching
  return `filing-calendar-preview-${corporationId}-${state}-${filingFrequency}-${taxPeriodStartMonth}-${params.taxPeriodEndMonth ?? ''}`;
}

function buildRequestBody(
  params: UseFilingCalendarPreviewParams
): FilingCalendarPreviewRequest | null {
  const {
    corporationId,
    state,
    filingFrequency,
    taxPeriodStartMonth,
    enabled,
  } = params;

  if (enabled === false) return null;
  if (!state || !filingFrequency || !taxPeriodStartMonth) return null;

  return {
    corporation_id: corporationId,
    state,
    filing_frequency:
      filingFrequency as FilingCalendarPreviewRequest['filing_frequency'],
    tax_period_start_month: taxPeriodStartMonth,
    ...(params.taxPeriodEndMonth && {
      tax_period_end_month: params.taxPeriodEndMonth,
    }),
  };
}

export function useFilingCalendarPreview(
  params: UseFilingCalendarPreviewParams
): UseFilingCalendarPreviewResult {
  const key = buildRequestKey(params);
  const body = buildRequestBody(params);

  const { data, error, mutate, isLoading } =
    useSWR<FilingCalendarPreviewResponse>(
      key,
      key && body
        ? async () => {
            const response = await fetcher(
              'new-sales-tax/filings/calendar/preview',
              {
                method: 'POST',
                body,
              }
            );
            return response.json();
          }
        : null
    );

  return {
    data: data?.data,
    isLoading,
    error,
    mutate,
  };
}
